import { Autocomplete, Box, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import ActivityLogsSection from '../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import EventsTimeline from '../components/common/eventTimeline/EventsTimeline';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import TagsSection from '../components/common/TagsSection/TagsSection';
import TextFieldWithLink from '../components/common/TextFieldWithLink/TextFieldWithLink';
import NotesSection from '../components/notes/NotesSection';
import { ROUTE_PATHS } from '../routes/Routes';
import { useArchiveEmployee, useCreateEmployee, useDestroyEmployee, useGetEmployeeById, useUpdateEmployee } from '../services/react-query/employees';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { useGetCurrentOrganization } from '../services/react-query/organization';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetAllUserRoles, useGetUserRoleById } from '../services/react-query/user_roles';
import { useGetCurrentUser } from '../services/react-query/users/useGetCurrentUser';
import {
  inputContainerStyling,
  inputDividerStyling,
  itemActivitySectionStyling,
  itemContainerStyling,
  itemInnerLayoutStyling,
  itemWrapperStyling,
} from '../styles/sxStyling';
import { ActivityLogOwnerType } from '../types/ActivityLog';
import { AttachmentOwnerType } from '../types/Attachment';
import { Employee } from '../types/Employee';
import { NoteableType } from '../types/Note';
import { PREFERENCES_VALID_KEYS, Preference } from '../types/Preference';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../types/User';
import { UserRole } from '../types/UserRole';
import { Loading } from '../uikit';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import PreferencePicker from '../components/preferences/picker/PreferencePicker';
import { DisableContext } from '../contexts/DisableContextProvider';
import FieldsSection from '../components/common/FieldsSection/FieldsSection';

const EmployeeItem: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const isNew = uuid === 'new';
  const templateUuid = searchParams.get('template') || undefined;

  const { mutate: deleteEmployee } = useDestroyEmployee({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.EMPLOYEES}`),
  });

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: allUserRolesData, isFetching: isGetAllRolesFetching } = useGetAllUserRoles({});
  const allUserRoles = allUserRolesData?.data || [];

  const { data: employeeData, isLoading: isGetEmployeeByIdLoading } = useGetEmployeeById({ uuid: _.toString(uuid), template: templateUuid });
  const { data: preferences, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({});
  const { data: currentUser } = useGetCurrentUser();

  const employeeUserExists = employeeData?.user_id !== undefined;
  const currentUserOrganizationUuid = currentUser?.organization_uuid;

  const { data: organizationData } = useGetCurrentOrganization({
    enabled: !_.isNil(currentUserOrganizationUuid) && employeeUserExists,
  });

  const { data: defaultUserRole } = useGetUserRoleById({
    enabled: organizationData?.default_role_uuid !== undefined,
    uuid: _.toString(organizationData?.default_role_uuid),
  });

  const saveCallback = () => setSaved(true);

  const { mutate: createEmployee } = useCreateEmployee({
    onSuccessCallback: (created: Employee) => {
      saveCallback();
      navigate(`/${ROUTE_PATHS.EMPLOYEES}/${created.uuid}`);
    },
  });

  const { mutate: updateEmployee } = useUpdateEmployee({
    uuid: _.toString(uuid),
    onSuccessCallback: saveCallback,
  });

  const isCreateEmployeeMutation = useIsMutating({ mutationKey: [mutationKeys.createEmployee] });
  const isUpdateEmployeeMutation = useIsMutating({ mutationKey: [mutationKeys.updateEmployee] });

  const { mutate: archiveEmployee } = useArchiveEmployee({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.EMPLOYEES}`),
  });

  useEffect(() => {
    if (employeeData) setEmployee(employeeData);
  }, [employeeData]);

  useEffect(() => {
    if (employee && (!employee.user_roles_uuids || employee.user_roles_uuids.length === 0) && defaultUserRole) {
      setEmployee({ ...employee, user_roles_uuids: [defaultUserRole.uuid] });
    }
  }, [employee, defaultUserRole]);

  const { disabled } = useContext(DisableContext);

  const selectedRoles = allUserRoles.filter((role) => employee?.user_roles_uuids?.includes(role.uuid)) || [];

  if (isGetEmployeeByIdLoading || !employee) return <Loading />;

  const updateEmployeeData = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmployee({ ...employee, [event.target.id]: event.target.value } as Employee);
  };

  const handleSave = () => {
    if (!employee) return;
    if (isNew) createEmployee(employee);
    else updateEmployee(employee);
  };

  const handleUserRoleChange = (_: SyntheticEvent<Element, Event>, selected: UserRole[]) => {
    setEmployee({ ...employee, user_roles_uuids: selected.map((r) => r.uuid) } as Employee);
  };

  // Extract title/role options
  const titleOptions = (preferences ?? []).filter((p) => p.key === PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE);
  const roleOptions = (preferences ?? []).filter((p) => p.key === PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE);
  const combinedHeader =
    `${[employeeData?.first_name, employeeData?.last_name].filter(Boolean).join(' ')} ` + `${employeeData?.email ? `(${employeeData.email})` : ''}`.trim();

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Employee' : combinedHeader}
        onSave={handleSave}
        disableActions={Boolean(isCreateEmployeeMutation || isUpdateEmployeeMutation)}
        archived={!!employee.discarded_at}
        showMoreOptions={!isNew ? <MoreOptions onDelete={deleteEmployee} onArchive={archiveEmployee} name={employee?.chosen_name} itemName="employee" /> : null}
        module={UserConfigurableModule.EMPLOYEES}
        infoItems={[{ content: employeeData?.title_value ?? '—' }]}
      />

      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Employee Information" collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_INFORMATION}>
            <Box sx={inputContainerStyling}>
              <Box sx={inputDividerStyling}>
                <TextField
                  id="chosen_name"
                  label="Chosen Name"
                  autoFocus
                  onChange={updateEmployeeData}
                  value={employee?.chosen_name ?? ''}
                  disabled={disabled}
                  {...errorPopulator(employee, 'chosen_name')}
                />
                <TextField
                  id="first_name"
                  label="First Name"
                  onChange={updateEmployeeData}
                  value={employee?.first_name ?? ''}
                  disabled={disabled}
                  {...errorPopulator(employee, 'first_name')}
                />
                <TextField
                  id="last_name"
                  label="Last Name"
                  onChange={updateEmployeeData}
                  value={employee?.last_name ?? ''}
                  disabled={disabled}
                  {...errorPopulator(employee, 'last_name')}
                />
                {/* Title (single) */}
                <PreferencePicker
                  label="Title"
                  options={titleOptions}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(preferences ?? [], employee?.title_uuid) as Preference | null}
                  onValueChange={(pref) => setEmployee((prev) => (prev ? ({ ...prev, title_uuid: (pref as Preference | null)?.uuid } as Employee) : prev))}
                  disabled={disabled}
                  error={!!errorPopulator(employee, 'title')?.error}
                  helperText={errorPopulator(employee, 'title')?.helperText}
                />
              </Box>
              <Box sx={inputDividerStyling}>
                <TextFieldWithLink
                  linkType="email"
                  id="email"
                  label="Email"
                  onChange={updateEmployeeData}
                  value={employee?.email ?? ''}
                  disabled={disabled}
                  {...errorPopulator(employee, 'email')}
                />
                <Box display="flex" gap={3}>
                  <TextFieldWithLink
                    linkType="phone"
                    id="phone"
                    label="Phone"
                    onChange={updateEmployeeData}
                    value={employee?.phone ?? ''}
                    disabled={disabled}
                    {...errorPopulator(employee, 'phone')}
                    fullWidth
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    id="extension"
                    label="Extension"
                    onChange={updateEmployeeData}
                    value={employee?.extension ?? ''}
                    disabled={disabled}
                    {...errorPopulator(employee, 'extension')}
                    fullWidth
                    sx={{ flex: 1 }}
                  />
                </Box>

                {/* Roles (multi) */}
                <PreferencePicker
                  label="Role"
                  options={roleOptions}
                  loading={isGetAllPreferencesFetching}
                  multiple
                  value={_.filter(preferences ?? [], (p) => (employee?.roles_uuids ?? []).includes(p.uuid))}
                  onValueChange={(vals) => {
                    const arr = (vals as Preference[] | null) ?? [];
                    setEmployee((prev) => (prev ? ({ ...prev, roles_uuids: arr.map((p) => p.uuid) } as Employee) : prev));
                  }}
                  disabled={disabled}
                  error={!!errorPopulator(employee, 'role')?.error}
                  helperText={errorPopulator(employee, 'role')?.helperText}
                  fullWidth
                />

                <TextField
                  id="code"
                  label="Code"
                  onChange={updateEmployeeData}
                  value={employee?.code ?? ''}
                  {...errorPopulator(employee, 'code')}
                  disabled={disabled}
                />

                {employee?.user_id || isNew ? (
                  <Autocomplete
                    multiple
                    options={allUserRoles}
                    value={selectedRoles}
                    loading={isGetAllRolesFetching}
                    getOptionLabel={(role) => role.name}
                    onChange={handleUserRoleChange}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    renderInput={(params) => <TextField {...params} label="Permissions" />}
                    disabled={disabled}
                  />
                ) : null}
              </Box>
            </Box>
          </AccordionSection>

          <FieldsSection
            fields={employee.fields}
            setFields={(fields) => setEmployee({ ...employee, fields })}
            collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_CUSTOM_FIELDS}
          />

          {!isNew && <EventsTimeline entityUuid={employee.uuid} entityType={'employees'} />}

          <TagsSection tags={employee.tags} collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_TAGS} setTags={(tags) => setEmployee({ ...employee, tags })} />

          {!isNew && (
            <AttachmentsSection
              collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_ATTACHMENTS}
              ownerType={AttachmentOwnerType.EMPLOYEE}
              ownerUuid={uuid ?? ''}
            />
          )}

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.EMPLOYEE} collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_ACTIVITY_LOG} />
            {uuid && (
              <NotesSection
                collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_NOTES}
                noteable={{
                  noteableUuid: uuid ?? '',
                  noteableType: NoteableType.EMPLOYEE,
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeItem;
